/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useGridRowsPerPage} from '@ion/applications/src/hooks/useGridRowsPerPage';
import {useViewSubGroups} from '@ion/applications/src/hooks/useViewSubGroups';
import {
  getBulkEntityDeleteMessage,
  getEntityDeleteMessage
} from '@ion/applications/src/utils/translation';
import useHasMounted from '@ion/core/src/hooks/useHasMounted';
import {translate} from '@ion/core/src/locale/locale';
import {UserContext} from '@ion/core/src/services/MicroServices';
import {
  AccountLink,
  CRUDPermissions,
  Entity,
  Feature,
  GenericObject,
  Link,
  User,
  UserFilters,
  Users
} from '@ion/core/src/types/entities';
import {DownloadTemplate} from '@ion/core/src/types/templates';
import {
  AuditDateFilterProps,
  AuditDialog
} from '@ion/dls/src/editors/audit/AuditDialog';
import {ViewAuditChangesDialog} from '@ion/dls/src/editors/audit/ViewAuditChangesDialog';
import {BulkDeletePrompt} from '@ion/dls/src/editors/BulkDeletePrompt';
import {MovedEntitiesEditor} from '@ion/dls/src/editors/MovedEntitiesEditor';
import {UserDeletePrompt} from '@ion/dls/src/editors/UserDeletePrompt';
import {IonSnackbar} from '@ion/dls/src/notifications/IonSnackbar';
import {
  ModularSearchProps,
  PageViewSubGroupsProps,
  ViewSubGroupsPosition
} from '@ion/dls/src/types/types';
import {
  getViewSubGroupsProps,
  partialStatusMessageFormatter
} from '@ion/dls/src/utils/ComponentUtils';
import BaseSearchMapping from '@ion/features/src/search/BaseSearchMappings';
import {
  AttributeOperators,
  AttributeTypes,
  DISABLED,
  ENABLED,
  Mapping,
  SuggestionsType,
  SUSPENDED
} from '@ion/features/src/search/types';
import useCurrentGlobalGroupId from '../../../hooks/useCurrentGlobalGroupId';
import {dispatchGridColumnsChange} from '../../../store/helpers';
import {ExportContext} from '../../../types/redux';
import {ReportOperations} from '../../reports/reportsUtils';
import AdminContainer, {
  GridOperations,
  PageTitle
} from '../AdminContainer';
import AddUserEditor from './AddUserEditor';
import AdminUsersGrid, {AdminUsersGridColumns} from './AdminUsersGrid';
import BulkEditUserEditor from './BulkEditUserEditor';
import BulkMoveUserEditor from './BulkMoveUserEditor';
import EditUserEditor from './EditUserEditor';
import ImportUsersContainer from './ImportUsersContainer';
import {
  addAdminUser,
  auditAdminUser,
  deleteAdminUser,
  deleteAdminUsers,
  deleteUser,
  deleteUsers,
  importAdminUsers,
  initializeAdminUsers,
  NAME,
  PREFERENCE,
  setUsersGridPageSize,
  setUserViewSubGroups,
  showAuditChangesDialog,
  UPDATE_GRID_COLUMNS,
  updateUsersSearchQueries
} from './state';
import useUserMove from './useUserMove';

export interface AdminUsersProps extends ReportOperations,
  PageViewSubGroupsProps,
  GridOperations,
  AuditDateFilterProps,
  ModularSearchProps {
  getRouteLabel: (route: string, useTitleCase: boolean) => string;
  onSelectAccount?: (value: AccountLink) => void;
  accountHierarchy?: AccountLink[];
  Features: Feature;
  defaultPageId: string;
  context?: UserContext;
  isCtcAdmin?: boolean;
  usePreferedPageSize?: boolean;
  enableSelectionsExport?: boolean;
  includeApplications?: boolean;
  includeAddresses?: boolean;
  includeContacts?: boolean;
  multipleAddress?: boolean;
  includeGlobalSearch?: boolean;
  searchKeyword?: string;
  account?: Link;
  globalGroup?: AccountLink;
  onUserMove?: boolean;
  enableAutoSave?: boolean;
  filteredColumns?: AdminUsersGridColumns[];
  usersPermissions?: CRUDPermissions;
  accountsPermissions?: CRUDPermissions;
  refreshInitialization?: Function;
}

// [ION-APP TODO]: Use Hooks to refactor this operation of modular search.
// Reference: packages/ion-ctc-admin/src/routes/provisioning/devicefiles/DeviceFiles.tsx
const usersMappings = (): {[key: string]: Mapping} => ({
  username : {
    type       : AttributeTypes.TEXT,
    attributes : {
      value : AttributeOperators.CONTAINS,
      label : translate('common:modularsearch.attributes.contains')
    },
    suggestion          : SuggestionsType.TEXT,
    label               : translate('admin-users:modularsearch.labels.username'),
    key                 : 'username',
    hasSpecialCharacter : true
  },
  firstName : {
    type       : AttributeTypes.TEXT,
    attributes : {
      value : AttributeOperators.CONTAINS,
      label : translate('common:modularsearch.attributes.contains')
    },
    suggestion : SuggestionsType.TEXT,
    label      : translate('admin-users:modularsearch.labels.firstName'),
    key        : 'firstName'
  },
  lastName : {
    type       : AttributeTypes.TEXT,
    attributes : {
      value : AttributeOperators.CONTAINS,
      label : translate('common:modularsearch.attributes.contains')
    },
    suggestion : SuggestionsType.TEXT,
    label      : translate('admin-users:modularsearch.labels.lastName'),
    key        : 'lastName'
  },
  status : {
    type       : AttributeTypes.SELECT,
    attributes : {
      value : AttributeOperators.IN,
      label : translate('common:modularsearch.attributes.in')
    },
    options : [
      {
        label : translate('common:modularsearch.status.Enabled'),
        value : ENABLED
      },
      {
        label : translate('common:modularsearch.status.Disabled'),
        value : DISABLED
      },
      {
        label : translate('common:modularsearch.status.Suspended'),
        value : SUSPENDED
      }
    ],
    suggestion : SuggestionsType.SELECTION,
    label      : translate('admin-users:modularsearch.labels.status'),
    key        : 'status'
  }
});

const uniqueConditions = (): string => {
  const Contains = translate('common:modularsearch.attributes.contains');
  const In = translate('common:modularsearch.attributes.in');
  const Is = translate('common:modularsearch.attributes.is');
  const Between = translate('common:modularsearch.attributes.between');
  return (`(${Contains}|${In}|${Is}|${Between})`);
};

/**
 * @AdminUsers
 *
 * Defines the AdminUsers container component. This component is only to render
 * child components and provide top level subscriptions and dispatches to the
 * redux store.
 */
export const AdminUsers = ({
  Features,
  getRouteLabel,
  defaultPageId,
  isCtcAdmin,
  enableSelectionsExport,
  context,
  includeApplications,
  includeAddresses,
  includeContacts,
  onSelectAccount,
  accountHierarchy,
  account,
  globalGroup,
  filteredColumns,
  usersPermissions,
  accountsPermissions,
  refreshInitialization,
  viewSubGroupsPosition = ViewSubGroupsPosition.GRID_PAGINATION_CONTROLS,
  searchKeyword = '',
  enableAutoSave = false,
  onUserMove = false,
  includeGlobalSearch = false,
  multipleAddress = false,
  enableSubgroupToggle = false,
  isSortByAlphaNumeric = false,
  enableDateFilter = false,
  usePreferedPageSize = false,
  autoSelectNext = false
}: AdminUsersProps) => {
  const dispatch             = useDispatch();
  const isMounted            = useHasMounted();
  const currentGlobalGroupId = useCurrentGlobalGroupId();

  const {
    result: deletedUser,
    selected,
    isEditing,
    loading,
    error
  } =  useSelector(({adminUsers: {destroy}}) => destroy);

  const {
    result: bulkDeleteResult,
    selected: bulkDeleteSelected,
    isEditing: bulkDeleteIsEditing,
    loading: bulkDeleteLoading,
    error: bulkDeleteError,
    record: bulkDeleteRecord
  } = useSelector(({adminUsers: {bulkDelete}}) => bulkDelete);

  const {
    isEditing: showAuditUser,
    selected: auditUser
  } = useSelector(({adminUsers: {audit}}) => audit);

  const gridRef                       = useRef<GenericObject>();
  const {isEditing: showAddUser}      = useSelector(({adminUsers: {add}}) => add);
  const {isEditing: showBulkEditUser} = useSelector(({adminUsers: {bulkEdit}}) => bulkEdit);
  const {isImporting: showImportUser} = useSelector(({adminUsers: {bulkImport}}) => bulkImport);
  const {columnState}                 = useSelector(({adminUsers: {grid}}) => grid);

  const {
    isEditing: showEditUser,
    isView: showViewUser
  } = useSelector(({adminUsers: {edit}}) => edit);

  const {
    auditRecord: selectedAuditRecord,
    showChangesDialog: shouldShowAuditChangesDialog
  } = useSelector(({adminUsers: {audit}}) => audit);

  const [viewSubGroups, toggleViewSubGroups] = useViewSubGroups({
    name       : NAME,
    action     : setUserViewSubGroups,
    view       : !Features || enableSubgroupToggle,
    preference : PREFERENCE
  });

  const [gridPageSize, setGridPageSize] = useGridRowsPerPage({
    name       : NAME,
    action     : setUsersGridPageSize,
    preference : PREFERENCE
  });

  const {
    isExporting,
    movedUsers,
    showSuccess,
    onCloseSnack,
    onViewRecords,
    showMoveUser,
    viewMovedUsers,
    movedUsersErrors,
    selectedUsersToMove,
    onCancelViewMovedUsersDialog
  } = useUserMove();

  useEffect(() => {
    if (!isMounted) {
      dispatch(initializeAdminUsers());
    }
  }, [isMounted]);

  /**
   * @private
   */
  const onImport = function() {
    dispatch(importAdminUsers() as any);
  };

  /**
   * @private
   */
  const onExport = function(downloadParams?: DownloadTemplate) {
    gridRef.current?.onExport(downloadParams);
  };

  /**
   * @private
   */
  const onRefresh = function() {
    gridRef.current?.onRefresh(true);
  };

  /**
   * @private
   */
  const toggleAddUserEditor = function() {
    dispatch(addAdminUser());
  };

  /**
   * @private
   */
  const onDeleteConfirm = function(user: User) {
    dispatch(deleteUser(selected) as any);
  };

  /**
   * @private
   */
  function onDeleteCancel() {
    dispatch(deleteAdminUser(selected, false));
  }

  /**
  * @private
  */
  const onBulkDeleteConfirm = function(users: Users) {
    dispatch(deleteUsers(bulkDeleteSelected) as any);
  };

  /**
 * @private
 */
  function onBulkDeleteCancel() {
    dispatch(deleteAdminUsers(bulkDeleteSelected, false));
  }

  // [APPLICATIONS PACKAGE TODO]: This appears to never have been implemented as the action
  // creator does not exist and was never imported, commenting out to resolve eslint errors.
  // see: http://bitbucket.pd.calampcorp.com:7990/projects/ION/repos/ion/diff/packages/applications/src/pages/admin/users/AdminUsers.tsx?until=99aa136cc24d4a91a6d2c430f62dfe55dfe81e16
  const onMovedUsersExportClick = useCallback((items) => {
    // dispatch(exportMovedUsers(items));
  }, []);

  /**
   * @private
   */
  function onAuditClose() {
    dispatch(auditAdminUser(null, false));
  }

  /**
   * @private
   */
  const onUpdateGridColumns = function() {
    dispatch(dispatchGridColumnsChange(columnState, NAME, UPDATE_GRID_COLUMNS) as any);
  };

  const getDeleteMessage = function() {
    if (isCtcAdmin) {
      if (selected && !bulkDeleteSelected) {
        return getEntityDeleteMessage(selected, true);
      }
      if (!selected && bulkDeleteSelected) {
        return getBulkEntityDeleteMessage(Entity.user, bulkDeleteSelected, true);
      }
    }
  };

  function onViewAuditChanges(auditRecord, show = false) {
    dispatch(showAuditChangesDialog(auditRecord, show));
  };

  function onViewAuditChangesClose() {
    dispatch(showAuditChangesDialog(null, false));
  };

  const getSearchParams = useCallback(function(filters: UserFilters) {
    dispatch(updateUsersSearchQueries(filters));
    onRefresh();
  }, []);

  const mappings = useMemo(() => {
    return new BaseSearchMapping(usersMappings(), uniqueConditions(), currentGlobalGroupId);
  }, [currentGlobalGroupId]);

  const viewSubGroupsProps = useMemo(() => getViewSubGroupsProps(
    viewSubGroupsPosition,
    viewSubGroups as boolean,
    toggleViewSubGroups as Function
  ), [viewSubGroupsPosition, viewSubGroups, toggleViewSubGroups]);

  return (
    <>
      <AdminContainer isCtcAdmin={isCtcAdmin}
        hasAddPermission={usersPermissions?.hasAddPermission}
        autoSelectNext={autoSelectNext}
        grid={<AdminUsersGrid ref={gridRef}
          onUserMove={onUserMove}
          isCtcAdmin={isCtcAdmin}
          Features={Features}
          usePreferedPageSize={usePreferedPageSize}
          isSortByAlphaNumeric={isSortByAlphaNumeric}
          filteredColumns={filteredColumns}
          includeGlobalSearch={includeGlobalSearch}
          searchKeyword={searchKeyword}
          usersPermissions={usersPermissions}
          accountsPermissions={accountsPermissions}
          enableSelectionsExport={enableSelectionsExport}
          onSelectAccount={onSelectAccount}
          accountHierarchy={accountHierarchy}
          gridPageSize={gridPageSize}
          setGridPageSize={setGridPageSize}
          refreshInitialization={refreshInitialization}
          {...viewSubGroupsProps[ViewSubGroupsPosition.GRID_PAGINATION_CONTROLS]} />}
        reducer={NAME}
        title={PageTitle.adminUsers}
        searchLabel={translate('admin-users:search')}
        onShowColumnChooser={gridRef.current?.toggleColumnChooser}
        exportContext={ExportContext.user}
        onImport={onImport}
        onExport={onExport}
        onRefresh={onRefresh}
        onAdd={toggleAddUserEditor}
        onUpdateGridColumns={onUpdateGridColumns}
        includeGlobalSearch={includeGlobalSearch}
        getSearchParams={getSearchParams}
        searchInstance={mappings}
        {...viewSubGroupsProps[ViewSubGroupsPosition.GRID_CTAS]}
        includeExport />
      {showAddUser && <AddUserEditor isCtcAdmin={isCtcAdmin}
        enableAutoSave={enableAutoSave}
        multipleAddress={multipleAddress}
        context={context}
        includeApplications={includeApplications}
        includeAddresses={includeAddresses}
        includeContacts={includeContacts}
        isViewDialog={showViewUser}
        getRouteLabel={getRouteLabel}
        defaultPageId={defaultPageId}/>}
      {(showEditUser || showViewUser) && <EditUserEditor defaultPageId={defaultPageId}
        multipleAddress={multipleAddress}
        isCtcAdmin={isCtcAdmin}
        context={context}
        includeApplications={includeApplications}
        includeAddresses={includeAddresses}
        includeContacts={includeContacts}
        getRouteLabel={getRouteLabel}
        usersPermissions={usersPermissions}
        isViewDialog={showViewUser} />}
      {showImportUser && <ImportUsersContainer context={context}
        onDownload={onExport} />}
      {showBulkEditUser && <BulkEditUserEditor context={context}/>}
      {showMoveUser && <BulkMoveUserEditor globalGroup={globalGroup}/>}
      {viewMovedUsers && (
        <MovedEntitiesEditor entity={translate('common:entityLabels.user')}
          isExporting={isExporting}
          movedEntities={movedUsers}
          errors={movedUsersErrors}
          onCancel={onCancelViewMovedUsersDialog}
          onMovedDevicesExportClick={onMovedUsersExportClick} />
      )}
      {showAuditUser && (
        <AuditDialog enableDateFilter={enableDateFilter}
          item={auditUser}
          rowMenusEnabled={isCtcAdmin}
          showDifferences={isCtcAdmin}
          entity={Entity.user}
          onViewAuditChanges={onViewAuditChanges}
          onCloseDialog={onAuditClose} />
      )}
      {shouldShowAuditChangesDialog && selectedAuditRecord && (
        <ViewAuditChangesDialog auditRecord={selectedAuditRecord}
          onCloseDialog={onViewAuditChangesClose}
          entity={Entity.user} />
      )}
      <UserDeletePrompt show={isEditing}
        entity={translate('common:entityLabels.user')}
        onDeleteConfirm={onDeleteConfirm}
        onDeleteCancel={onDeleteCancel}
        overrideMessage={getDeleteMessage}
        user={selected}
        result={deletedUser}
        loading={loading}
        error={error} />
      {(bulkDeleteIsEditing || bulkDeleteRecord) &&
          (<BulkDeletePrompt show={bulkDeleteIsEditing}
            items={bulkDeleteSelected}
            context={context}
            overrideMessage={getDeleteMessage}
            entity={translate('common:entityLabels.users')}
            result={bulkDeleteResult}
            loading={bulkDeleteLoading}
            error={bulkDeleteError}
            deletedItems={bulkDeleteRecord}
            onDeleteConfirm={onBulkDeleteConfirm}
            onDeleteCancel={onBulkDeleteCancel} />)}
      {showSuccess && (
        <IonSnackbar show
          message={translate('common:editors.shared.successMoved', {
            entity : translate('common:entityLabels.users')
          })}
          partialSuccessState={partialStatusMessageFormatter(movedUsers, selectedUsersToMove)}
          customDismissLabel={translate('common:dismiss')}
          onDismiss={onCloseSnack} />
      )}
    </>
  );
};
export default AdminUsers;
